import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Spinner,
  Flex,
  Button,
  Checkbox,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  IconButton,
  Tooltip,
  ModalFooter
} from "@chakra-ui/react";
import { RiSearchLine } from "react-icons/ri";
import { ChevronDownIcon, DeleteIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { deleteAnalysisTreeByID, getTreeHeaders } from "../../services/AnalysisTreeServices";
import { listMetrics } from "../../services/MetricService";
import { Header } from "build-ts/metadata";
import { getSortByLabel } from "../../utils/analysisUtils";
import showToast from "hooks/useCustomToast";
import CustomModal from "../modals/CustomModal";
import ReactMarkdown from "react-markdown";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { useUser } from "context/UserContext";

interface LibraryAnalysesComponentProps {
  onSelectionChange?: (selectedItems: Set<string>) => void;
  selectedItems?: Set<string>;
  hideActions?: boolean;
}

const LibraryAnalysesComponent: React.FC<LibraryAnalysesComponentProps> = ({
  onSelectionChange,
  selectedItems: externalSelectedItems,
  hideActions = false
}) => {
  const { defaultTimeZone } = useUser();
  const [trees, setTrees] = useState<Header[]>([]);
  const [metricHeaders, setMetricHeaders] = useState<Header[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchString, setSearchString] = useState("");
  const [submittedSearchString, setSubmittedSearchString] = useState("");
  const [sortBy, setSortBy] = useState("CREATED");
  const [sortAscending, setSortAscending] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedTrees, setSelectedTrees] = useState<Set<string>>(externalSelectedItems || new Set());
  const [searchMetrics, setSearchMetrics] = useState<string[]>([]);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const pageSize = 20;

  const fetchTrees = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getTreeHeaders(sortBy, searchMetrics, pageSize, currentPage, submittedSearchString, sortAscending);
      if (data) {
        setTrees(data?.headers || []);
        setTotalRows(data?.totalRows || 0);
      }
    } catch (error) {
      console.error("Error fetching trees:", error);
      showToast("Error fetching trees", "Failed to load analysis trees", "error");
    } finally {
      setIsLoading(false);
    }
  }, [sortBy, searchMetrics, pageSize, currentPage, submittedSearchString, sortAscending]);

  useEffect(() => {
    fetchMetricHeaders();
  }, []);

  useEffect(() => {
    fetchTrees();
  }, [fetchTrees]);

  // Sync external selection changes
  useEffect(() => {
    if (externalSelectedItems) {
      setSelectedTrees(externalSelectedItems);
    }
  }, [externalSelectedItems]);

  const fetchMetricHeaders = async () => {
    try {
      const data = await listMetrics(""); // Pass empty string as modelId
      if (data) {
        setMetricHeaders(data);
      }
    } catch (error) {
      console.error("Error fetching metric headers:", error);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setSubmittedSearchString(searchString);
      setCurrentPage(1); // Reset to first page when searching
    }
  };



  const handleMetricChange = (metricId: string) => {
    setSearchMetrics((prevMetrics) => {
      const newMetrics = prevMetrics.includes(metricId)
        ? prevMetrics.filter((id) => id !== metricId)
        : [...prevMetrics, metricId];
      return newMetrics;
    });
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleSelectAllMetrics = () => {
    if (searchMetrics.length === metricHeaders.length) {
      // If all are selected, deselect all
      setSearchMetrics([]);
      console.log("Deselected all metrics");
    } else {
      // Select all metrics
      const allMetricIds = metricHeaders.map(header => header.id!);
      setSearchMetrics(allMetricIds);
      console.log("Selected all metrics:", allMetricIds);
    }
    setCurrentPage(1); // Reset to first page when filtering
  };

  const formatDate = (dateMs: number | undefined) => {
    if (!dateMs) return "N/A";
    try {
      return `${epochConverterWithTimeZone(dateMs || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)} ${defaultTimeZone.toUpperCase()}`;
    } catch {
      return "N/A";
    }
  };

  // Handle individual tree selection
  const handleTreeSelection = (treeId: string) => {
    const newSelectedTrees = new Set(selectedTrees);
    if (newSelectedTrees.has(treeId)) {
      newSelectedTrees.delete(treeId);
    } else {
      newSelectedTrees.add(treeId);
    }
    setSelectedTrees(newSelectedTrees);
    onSelectionChange?.(newSelectedTrees);
  };

  // Handle select all/none
  const handleSelectAll = () => {
    const newSelectedTrees = selectedTrees.size === trees.length
      ? new Set<string>()
      : new Set(trees.map(tree => tree.id!));
    setSelectedTrees(newSelectedTrees);
    onSelectionChange?.(newSelectedTrees);
  };

  const handleDeleteAnalysis = async (id: string) => {
    setOpenDeleteModal(true);
    setDeleteId(id);
  };

  const handleDelete = async () => {
    if (deleteId) {
      await deleteAnalysisTreeByID(deleteId);
      showToast("Analysis deleted successfully", "", "success");
      setOpenDeleteModal(false);
      fetchTrees();
    }
  };

  // Check if all trees are selected
  const isAllSelected = trees.length > 0 && selectedTrees.size === trees.length;
  const isIndeterminate = selectedTrees.size > 0 && selectedTrees.size < trees.length;



  return (
    <Box p={6} height="100%" overflowY="auto">
      {/* Search and Filter Section */}
      <HStack spacing={4} mb={6} justify="space-between">
        <HStack spacing={4}>
          <InputGroup size="md" maxW="400px">
            <InputLeftElement>
              <Icon as={RiSearchLine} color="gray.500" />
            </InputLeftElement>
            <Input
              placeholder="Search trees..."
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              focusBorderColor="purple.600"
            />
          </InputGroup>

          {/* Filter by Metric */}
          <Flex align="center">
            <Text
              align="center"
              mb={0}
              mr={2}
              fontSize="sm"
              fontWeight="medium"
              color="fg.muted"
              whiteSpace="nowrap"
            >
              Filter by:
            </Text>
            <Menu closeOnSelect={false} preventOverflow={true}>
              <MenuButton
                as={Button}
                color="fg.default"
                bg="bg.default"
                maxW="100%"
                width="auto"
                rightIcon={<ChevronDownIcon color="fg.muted" />}
                fontSize="sm"
                size="md"
                border="1px"
                borderColor="gray.300"
                borderRadius="md"
                fontWeight="normal"
                _hover={{ bg: "purple.50" }}
                p={3}
              >
                {metricHeaders && metricHeaders.length > 0
                  ? `Selected (${searchMetrics.length})`
                  : "Select Metrics"}
              </MenuButton>
              <MenuList maxHeight="200px" overflowY="auto" ml={-6}>
                {/* Select All Option */}
                <MenuItem minH="40px" zIndex={10} borderBottom="1px" borderColor="gray.200">
                  <Checkbox
                    isChecked={metricHeaders && searchMetrics.length === metricHeaders.length}
                    isIndeterminate={metricHeaders && searchMetrics.length > 0 && searchMetrics.length < metricHeaders.length}
                    onChange={handleSelectAllMetrics}
                    colorScheme="purple"
                    fontWeight="semibold"
                  >
                    Select All
                  </Checkbox>
                </MenuItem>
                {metricHeaders &&
                  metricHeaders.map((header: Header, index) => (
                    <MenuItem key={index} minH="40px" zIndex={10}>
                      <Checkbox
                        isChecked={searchMetrics.includes(header.id!)}
                        onChange={() => handleMetricChange(header.id!)}
                        colorScheme="purple"
                      >
                        {header.name}
                      </Checkbox>
                    </MenuItem>
                  ))}
              </MenuList>
            </Menu>
          </Flex>

          {/* Sort By Toggle */}
          <Flex align="center">
            <Text
              align="center"
              mb={0}
              mr={2}
              fontSize="sm"
              fontWeight="medium"
              color="fg.muted"
              whiteSpace="nowrap"
            >
              Sort by:
            </Text>
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon color="fg.muted" />}
                fontSize="xs"
                size="xs"
                rounded="md"
                color="fg.default"
                bg="bg.default"
                border="1px"
                borderColor="gray.300"
                _hover={{ bg: "purple.50" }}
                p={3}
                aria-label="Sort by"
                textAlign="left"
                width="150px"
                isTruncated
              >
                {getSortByLabel(sortBy)} {sortAscending ? "Asc" : "Desc"}

              </MenuButton>
              <MenuList>
                <MenuItem key="last_modified_asc"
                  onClick={() => {
                    setSortBy("LAST_MODIFIED");
                    setSortAscending(true);
                  }}
                  fontSize="small"
                >
                  Last Modified (Asc)
                </MenuItem>
                <MenuItem key="last_modified_desc"
                  onClick={() => {
                    setSortBy("LAST_MODIFIED");
                    setSortAscending(false);
                  }}
                  fontSize="small"
                >
                  Last Modified (Desc)
                </MenuItem>
                <MenuItem key="created_asc"
                  onClick={() => {
                    setSortBy("CREATED");
                    setSortAscending(true);
                  }}
                  fontSize="small"
                >
                  Created At (Asc)
                </MenuItem>
                <MenuItem key="created_desc"
                  onClick={() => {
                    setSortBy("CREATED");
                    setSortAscending(false);
                  }}
                  fontSize="small"
                >
                  Created At (Desc)
                </MenuItem>
                <MenuItem key="name_asc"
                  onClick={() => {
                    setSortBy("NAME");
                    setSortAscending(true);
                  }}
                  fontSize="small"
                >
                  Name (Asc)
                </MenuItem>
                <MenuItem key="name_desc"
                  onClick={() => {
                    setSortBy("NAME");
                    setSortAscending(false);
                  }}
                  fontSize="small"
                >
                  Name (Desc)
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
          {selectedTrees.size > 0 && !hideActions && (
            <Button
              colorScheme="purple"
              size="md"
              onClick={() => {
                // Open all selected trees in new tabs
                const treeIds = Array.from(selectedTrees);
                const url = `/analyze?tree_ids=${treeIds.join(",")}`;
                window.open(url, "_blank");
              }}
              isDisabled={selectedTrees.size === 0}
              _disabled={{
                opacity: 0.6,
                cursor: "not-allowed"
              }}
            >
              Open {selectedTrees.size > 0 ? `(${selectedTrees.size})` : ""}
            </Button>
          )}
        </HStack>

        <HStack spacing={4}>
          {selectedTrees.size > 0 && !hideActions && (
            <Text fontSize="sm" color="purple.600" fontWeight="medium" m={0}>
              {selectedTrees.size} selected
            </Text>
          )}
          <Text fontSize="sm" color="fg.muted" mb={0}>
            {trees.length} results
          </Text>
        </HStack>
      </HStack>

      {/* Table */}
      <TableContainer
        bg="white"
        borderRadius="lg"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
      >
        {isLoading ? (
          <Flex justify="center" align="center" p={8}>
            <Spinner size="lg" color="purple.500" />
          </Flex>
        ) : (
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm" width="50px">
                  <Checkbox
                    isChecked={isAllSelected}
                    isIndeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                    colorScheme="purple"
                  />
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Tree Name
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Description
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Author
                </Th>
                <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                  Created Date
                </Th>
                {!hideActions && (
                  <Th color="fg.muted" fontWeight="semibold" fontSize="sm">
                    {/* for delete */}
                  </Th>
                )}
              </Tr>
            </Thead>
            <Tbody>
              {trees.length === 0 ? (
                <Tr>
                  <Td colSpan={hideActions ? 4 : 5} textAlign="center" py={8} color="fg.muted">
                    No trees found
                  </Td>
                </Tr>
              ) : (
                trees.map((tree) => (
                  <Tr
                    key={tree.id}
                    _hover={{ bg: "gray.50" }}
                    bg={selectedTrees.has(tree.id!) ? "purple.50" : "white"}
                  >
                    <Td>
                      <Checkbox
                        isChecked={selectedTrees.has(tree.id!)}
                        onChange={() => handleTreeSelection(tree.id!)}
                        colorScheme="purple"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Td>
                    <Td
                      fontWeight="medium"
                      color="fg.default"
                    >
                      <Text mb={0}>{tree.name || "Unnamed Tree"}</Text>
                    </Td>
                    <Td
                      color="fg.muted"
                      width="300px"
                      maxW="300px"
                    >
                     <Tooltip
                        label={
                          <Box maxW="400px">
                            <ReactMarkdown>
                              {tree.description || ""}
                            </ReactMarkdown>
                          </Box>
                        }
                        hasArrow
                        placement="top"
                        isDisabled={!tree.description}
                        bg="gray.700"
                        color="white"
                        fontSize="sm"
                      >
                        <Box
                          fontSize="sm"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                          cursor={tree.description ? "pointer" : "default"}
                          sx={{
                            "& p": {
                              margin: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }
                          }}
                        >
                          {tree.description === null || tree.description === undefined || tree.description === "" ? "" : tree.description}
                        </Box>
                      </Tooltip>
                    </Td>
                    <Td
                      color="fg.muted"
                    >
                      {tree.author_name || "Unknown"}
                    </Td>
                    <Td
                      color="fg.muted"
                    >
                      {formatDate(tree.created_date_ms)}
                    </Td>
                    {!hideActions && (
                      <Td>
                        <HStack spacing={2}>
                          <Tooltip label="Open Analysis">
                            <IconButton
                              aria-label="Open Analysis"
                              icon={<ExternalLinkIcon />}
                              size="sm"
                              bg="none"
                              color="grey"
                              _hover={{ color: "purple.500" }}
                              onClick={() => {
                                // Navigate to plan page
                                window.open(`/analyze?metadataId=${tree.id}&metadataType=ANALYSIS_TREE`, "_blank");
                              }}
                            />
                          </Tooltip>
                          <Tooltip label="Delete Analysis">
                          <IconButton
                            aria-label="Delete Analysis"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            bg="none"
                            color="grey"
                            _hover={{ color: "red.500" }}
                            onClick={() => handleDeleteAnalysis(tree.id!)}
                          />
                        </Tooltip>
                        </HStack>
                      </Td>
                    )}
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      {!isLoading && totalRows > 0 && (
        <HStack justify="center" mt={6} spacing={2}>
          {Array.from({ length: Math.ceil(totalRows / pageSize) }, (_, index) => index + 1).map((pageNumber) => (
            <Button
              key={pageNumber}
              size="sm"
              variant={currentPage === pageNumber ? "solid" : "outline"}
              colorScheme={currentPage === pageNumber ? "purple" : "gray"}
              onClick={() => setCurrentPage(pageNumber)}
              minW="40px"
            >
              {pageNumber}
            </Button>
          ))}
        </HStack>
      )}

      {/* Delete Modal */}
      <CustomModal
        isOpen={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        title="Are you sure want to delete the analysis?"
        size="sm"
        maxWidth="600px"
        maxHeight="200px"
        showCloseButton={false}
      >
        <ModalFooter>
          <Button colorScheme="red" mr={3} onClick={() => handleDelete()}>
            Delete
          </Button>
          <Button colorScheme="purple" onClick={() => setOpenDeleteModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </CustomModal>
    </Box>
  );
};

export default LibraryAnalysesComponent;